import { useState } from "react";

import { apiClient, type Finding, type ReviewResponse } from "../../api/client";
import { AmazonInsights } from "./AmazonInsights";
import type { FlowState } from "./types";

interface SummaryStepProps {
  state: FlowState;
  onSelect: (finding: Finding) => void;
  onReview: (decision: "accepted" | "rejected", note: string) => Promise<void>;
}

export function SummaryStep({ state, onSelect, onReview }: SummaryStepProps) {
  const metrics = flattenMetrics(state.summary?.metrics ?? {});
  return (
    <div className="summary-layout">
      <section className="flow-panel summary-main">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">第 4 步</span>
            <h2>Amazon 运营周报</h2>
          </div>
          {state.run?.artifact_id && (
            <a className="primary-link" href={apiClient.artifactDownloadUrl(state.run.artifact_id)}>
              导出 JSON 报告
            </a>
          )}
        </div>
        <div className="metric-grid">
          {metrics.slice(0, 8).map(([label, value]) => (
            <article key={label}>
              <span>{label}</span>
              <strong>{String(value ?? "—")}</strong>
            </article>
          ))}
        </div>
        <div className="finding-heading">
          <div>
            <h3>异常与数据状态</h3>
            <p>按规则严重度稳定排序，点击查看阈值、中间值和证据。</p>
          </div>
          <span>{state.findings.length} 条</span>
        </div>
        <div className="finding-list">
          {state.findings.map((finding, index) => (
            <FindingRow
              finding={finding}
              index={index}
              key={finding.finding_key}
              onSelect={onSelect}
              review={state.reviews[finding.finding_key]}
              selected={state.selected?.finding_key === finding.finding_key}
            />
          ))}
        </div>
        <AmazonInsights advisory={state.advertisingAdvisory} searchTerms={state.searchTerms} />
      </section>
      <FindingDetail
        busy={state.busy}
        error={state.error}
        finding={state.selected}
        onReview={onReview}
        review={state.selected ? state.reviews[state.selected.finding_key] : undefined}
      />
    </div>
  );
}

function FindingRow({
  finding,
  index,
  onSelect,
  review,
  selected,
}: {
  finding: Finding;
  index: number;
  onSelect: (finding: Finding) => void;
  review?: ReviewResponse;
  selected: boolean;
}) {
  const payload = finding.finding;
  const status = String(payload.status ?? "unknown");
  return (
    <button
      className={selected ? "finding-row selected" : "finding-row"}
      onClick={() => onSelect(finding)}
      type="button"
    >
      <span className="rank">{index + 1}</span>
      <span className="finding-copy">
        <strong>{String(payload.sku ?? "未知 SKU")}</strong>
        <small>{label(String(payload.family ?? payload.code ?? "finding"))}</small>
      </span>
      {review && (
        <span className="status-pill reviewed">
          已{review.decision === "accepted" ? "确认" : "拒绝"}
        </span>
      )}
      <span
        className={
          status === "insufficient_data" ? "status-pill insufficient" : "status-pill danger"
        }
      >
        {status === "insufficient_data"
          ? "insufficient_data"
          : `严重度 ${String(payload.severity ?? 0)}`}
      </span>
    </button>
  );
}

function FindingDetail({
  busy,
  error,
  finding,
  onReview,
  review,
}: {
  busy: boolean;
  error: string | null;
  finding: Finding | null;
  onReview: (decision: "accepted" | "rejected", note: string) => Promise<void>;
  review?: ReviewResponse;
}) {
  const [note, setNote] = useState("");
  if (!finding) {
    return (
      <aside className="flow-panel detail-panel empty-detail">
        <p>选择一条异常查看可追溯详情。</p>
      </aside>
    );
  }
  const payload = finding.finding;
  return (
    <aside className="flow-panel detail-panel">
      <span className="eyebrow">异常详情</span>
      <h2>{String(payload.sku ?? "未知 SKU")}</h2>
      <span
        className={
          payload.status === "insufficient_data" ? "status-pill insufficient" : "status-pill danger"
        }
      >
        {String(payload.status ?? "unknown")}
      </span>
      <DetailGroup label="实测值" value={payload.measured} />
      <DetailGroup label="对比基线" value={payload.comparison} />
      <DetailGroup label="规则阈值" value={payload.thresholds} />
      <DetailGroup label="中间计算" value={payload.intermediate} />
      <div className="detail-group">
        <strong>证据引用</strong>
        <p>
          {Array.isArray(payload.evidence_links) && payload.evidence_links.length
            ? `${payload.evidence_links.length} 个受保护来源`
            : "数据不足，未形成结论证据"}
        </p>
      </div>
      <label className="review-note">
        评审备注（可选）
        <textarea
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          value={note}
        />
      </label>
      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}
      <div className="review-actions">
        <button disabled={busy} onClick={() => void onReview("accepted", note)} type="button">
          确认异常
        </button>
        <button
          className="secondary-button"
          disabled={busy}
          onClick={() => void onReview("rejected", note)}
          type="button"
        >
          拒绝异常
        </button>
      </div>
      {review && (
        <p className="review-saved">
          已保存第 {review.revision} 版评审：{review.decision}
        </p>
      )}
    </aside>
  );
}

function DetailGroup({ label: heading, value }: { label: string; value: unknown }) {
  const rows = isObject(value) ? Object.entries(value) : [];
  return (
    <div className="detail-group">
      <strong>{heading}</strong>
      {rows.length === 0 ? (
        <p>无可用数据</p>
      ) : (
        rows.map(([key, item]) => (
          <p key={key}>
            <span>{label(key)}</span>
            <b>{String(item ?? "—")}</b>
          </p>
        ))
      )}
    </div>
  );
}

function flattenMetrics(value: Record<string, unknown>): Array<[string, unknown]> {
  const rows: Array<[string, unknown]> = [];
  for (const [group, entry] of Object.entries(value)) {
    if (isObject(entry)) {
      for (const [key, item] of Object.entries(entry))
        rows.push([`${label(group)} · ${label(key)}`, item]);
    } else rows.push([label(group), entry]);
  }
  return rows;
}

function label(value: string): string {
  return value.replaceAll("_", " ");
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
